import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  Layers,
  Minus,
  RotateCw,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { QuotePdfButton } from "@/components/admin/QuotePdfButton";
import { adminApi, messageFrom } from "@/lib/api";
import { formatCount, formatDate, money } from "@/lib/format";

/**
 * Every quote one customer has had, side by side, oldest first.
 *
 * Built for the one question a repeat customer always asks: "why is it more than
 * last time?". Each column carries the fee, how it moved since their previous
 * quote for the SAME service on the SAME billing basis, and the two figures that
 * drive the price - turnover and monthly transaction volume - so the answer is
 * on screen instead of buried in three PDFs.
 *
 * A once-off financial statement fee is never subtracted from a monthly
 * bookkeeping retainer: the server only pairs like with like, and a quote with
 * no comparable predecessor says so rather than showing a meaningless delta.
 */

const DIRECTION = {
  up: {
    icon: TrendingUp,
    classes: "border-[color:var(--tt-warn-border)] bg-[color:var(--tt-warn-bg)] text-[color:var(--tt-warn-text)]",
    word: "up",
  },
  down: {
    icon: TrendingDown,
    classes: "border-emerald-200 bg-emerald-50 text-emerald-800",
    word: "down",
  },
  flat: {
    icon: Minus,
    classes: "border-border bg-muted text-muted-foreground",
    word: "unchanged",
  },
};

const signed = (amount) => `${amount > 0 ? "+" : ""}${money(amount)}`;

const percentText = (percent) => {
  if (percent === null || percent === undefined) return null;
  return `${percent > 0 ? "+" : ""}${percent}%`;
};

const ChangeBadge = ({ change, testId }) => {
  const tone = DIRECTION[change?.direction] || DIRECTION.flat;
  const Icon = tone.icon;
  const pct = percentText(change?.percent);

  return (
    <Badge
      variant="outline"
      data-testid={testId}
      className={`gap-1 px-1.5 py-0 text-[10px] font-semibold ${tone.classes}`}
    >
      <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
      {change.direction === "flat" ? "No change" : signed(change.amount)}
      {pct && change.direction !== "flat" ? (
        <span className="font-normal">({pct})</span>
      ) : null}
    </Badge>
  );
};

const Driver = ({ label, value, suffix = "" }) => (
  <div className="flex items-baseline justify-between gap-2">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="font-amount font-semibold text-foreground">
      {value === null || value === undefined ? "\u2014" : `${value}${suffix}`}
    </dd>
  </div>
);

const TrackSummary = ({ track }) => (
  <div
    data-testid="quote-comparison-track"
    data-track={track.key}
    className="rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-3 py-2.5"
  >
    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[color:var(--tt-teal-deep)]">
      {track.service_label} · {track.basis_label} · {track.count}{" "}
      {track.count === 1 ? "quote" : "quotes"}
    </p>
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <span className="font-amount text-sm font-bold text-[color:var(--tt-teal-deep)]">
        {money(track.first_amount)}
      </span>
      <ArrowRight
        className="h-3.5 w-3.5 shrink-0 text-[color:var(--tt-teal-deep)]"
        aria-hidden="true"
      />
      <span
        data-testid="quote-comparison-track-latest"
        className="font-amount text-sm font-bold text-[color:var(--tt-teal-deep)]"
      >
        {money(track.latest_amount)}
      </span>
      {track.change ? (
        <ChangeBadge change={track.change} testId="quote-comparison-track-change" />
      ) : (
        <span className="text-[11px] text-[color:var(--tt-teal-deep)]">
          Only quoted once — nothing to trend yet
        </span>
      )}
    </div>
    {track.change ? (
      <p className="mt-1 text-[11px] text-[color:var(--tt-teal-deep)]">
        {formatDate(track.first_at)} to {formatDate(track.latest_at)}
        {track.span_days ? ` · over ${track.span_days} day${track.span_days === 1 ? "" : "s"}` : null}
      </p>
    ) : null}
  </div>
);

const QuoteColumn = ({ quote, anchorRef }) => {
  const isAnchor = quote.quote_ref === anchorRef;

  return (
    <article
      data-testid="quote-comparison-column"
      data-quote-ref={quote.quote_ref}
      className={`flex w-[232px] shrink-0 flex-col rounded-lg border bg-white p-3 ${
        isAnchor
          ? "border-[color:var(--tt-teal-line)] shadow-[0_6px_20px_rgba(21,32,38,0.08)]"
          : "border-border"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <Badge
          variant="outline"
          data-testid="quote-comparison-sequence"
          className="gap-1 border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-1.5 py-0 text-[10px] font-semibold text-[color:var(--tt-teal-deep)]"
        >
          <Layers className="h-2.5 w-2.5" aria-hidden="true" />
          Quote {quote.quote_number} of {quote.quote_count}
        </Badge>
        {isAnchor ? (
          <span
            data-testid="quote-comparison-anchor"
            className="text-[10px] font-semibold uppercase tracking-[0.1em] text-primary"
          >
            Open
          </span>
        ) : null}
      </div>

      <p className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <CalendarDays className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
        <span data-testid="quote-comparison-date">{formatDate(quote.created_at)}</span>
      </p>
      <p
        data-testid="quote-comparison-reference"
        className="mt-0.5 truncate font-amount text-[10px] text-muted-foreground"
      >
        {quote.quote_ref}
      </p>

      <p className="mt-2 truncate text-[11px] font-semibold text-foreground" title={quote.service_label}>
        {quote.service_label}
      </p>
      <p
        data-testid="quote-comparison-amount"
        className="mt-1 font-amount text-lg font-bold text-primary"
      >
        {money(quote.amount)}
        <span className="text-[11px] font-normal text-muted-foreground">
          {quote.pricing_type === "monthly" ? "/month" : " once-off"}
        </span>
      </p>

      <div className="mt-2">
        {quote.change ? (
          <ChangeBadge change={quote.change} testId="quote-comparison-change" />
        ) : (
          <span
            data-testid="quote-comparison-no-change"
            className="text-[10px] leading-snug text-muted-foreground"
          >
            First {quote.basis_label} {quote.service_label} quote — nothing to compare
          </span>
        )}
      </div>
      {quote.change?.compared_to_number ? (
        <p className="mt-1 text-[10px] text-muted-foreground">
          vs quote {quote.change.compared_to_number}
          {quote.change.days_since
            ? ` · ${quote.change.days_since} day${quote.change.days_since === 1 ? "" : "s"} later`
            : null}
        </p>
      ) : null}

      <dl
        data-testid="quote-comparison-drivers"
        className="mt-3 space-y-1 border-t border-border pt-2.5 text-[11px]"
      >
        <Driver label="Turnover" value={money(quote.drivers?.turnover)} />
        <Driver
          label="Transactions"
          value={formatCount(quote.drivers?.transactions_per_month)}
          suffix={quote.drivers?.transactions_per_month ? "/m" : ""}
        />
      </dl>

      <div className="mt-auto space-y-2 pt-3">
        <Badge
          variant="outline"
          data-testid="quote-comparison-stage"
          className="w-full justify-center border-border px-1.5 py-0 text-[10px] font-semibold text-muted-foreground"
        >
          {quote.status_label}
        </Badge>
        <QuotePdfButton quoteRef={quote.quote_ref} className="w-full bg-white text-[12px]" />
      </div>
    </article>
  );
};

export const QuoteComparison = ({ quoteRef }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = useCallback(() => {
    if (!quoteRef) return undefined;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    adminApi
      .leadHistory(quoteRef)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            loading: false,
            data: null,
            error: messageFrom(err, "We couldn't load this customer's other quotes."),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [quoteRef]);

  useEffect(() => load(), [load]);

  if (state.loading) {
    return (
      <div data-testid="quote-comparison-loading" className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <div className="flex gap-3">
          <Skeleton className="h-64 w-[232px] shrink-0" />
          <Skeleton className="h-64 w-[232px] shrink-0" />
          <Skeleton className="h-64 w-[232px] shrink-0" />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div
        data-testid="quote-comparison-error"
        className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-3"
      >
        <p className="flex items-start gap-2 text-[13px] font-medium text-destructive">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {state.error}
        </p>
        <Button
          type="button"
          variant="outline"
          data-testid="quote-comparison-retry"
          onClick={load}
          className="mt-3 h-9 bg-white text-[13px]"
        >
          <RotateCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
          Try again
        </Button>
      </div>
    );
  }

  const data = state.data || {};
  const quotes = data.quotes || [];

  if (!quotes.length) {
    return (
      <p data-testid="quote-comparison-empty" className="text-sm text-muted-foreground">
        No other quotes stored for this customer yet.
      </p>
    );
  }

  return (
    <div data-testid="quote-comparison" className="min-w-0 space-y-4">
      <p
        data-testid="quote-comparison-summary"
        className="text-[13px] leading-relaxed text-muted-foreground"
      >
        <span className="font-semibold text-foreground">
          {data.quote_count} {data.quote_count === 1 ? "quote" : "quotes"}
        </span>{" "}
        for {data.customer?.full_name}
        {data.customer?.company ? ` · ${data.customer.company}` : ""} between{" "}
        {formatDate(data.first_quoted_at)} and {formatDate(data.latest_quoted_at)}. Fees are only
        compared against the same service on the same billing basis.
      </p>

      {(data.tracks || []).length ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {data.tracks.map((track) => (
            <TrackSummary key={track.key} track={track} />
          ))}
        </div>
      ) : null}

      <div
        data-testid="quote-comparison-columns"
        className="flex min-w-0 max-w-full gap-3 overflow-x-auto pb-2"
      >
        {quotes.map((quote) => (
          <QuoteColumn key={quote.quote_ref} quote={quote} anchorRef={data.anchor_ref} />
        ))}
      </div>
    </div>
  );
};
